import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  FolderKanban, Plus, Search, ChevronRight, ArrowLeft, Send,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import type { Database } from "@/lib/database.types";
import { useRequireAuth, hasRole } from "@/lib/auth";
import {
  Card, Pill, Button, Spinner, EmptyState,
} from "@/components/ui";
import { fmtMoney, fmtDate } from "@/lib/dates";
import AssignPersonalBookingModal from "@/features/schedule/AssignPersonalBookingModal";

// =============================================================================
// Projects list
// =============================================================================
// Reads projects + joined client/site/carrier/PM.
// Director / manager see all fields incl. contract value; supervisor / field /
// subcontractor see no financial column.
// "Active" filter covers scheduled + mobilising + live + acceptance_pending.
// =============================================================================

const STATUS_FILTERS = [
  { v: "active",             label: "Active" },
  { v: "scheduled",          label: "Scheduled" },
  { v: "live",               label: "Live" },
  { v: "mobilising",         label: "Mobilising" },
  { v: "acceptance_pending", label: "Acceptance" },
  { v: "on_hold",            label: "On hold" },
  { v: "closed",             label: "Closed" },
  { v: "all",                label: "All" },
] as const;

export default function ProjectsListScreen() {
  const user = useRequireAuth();
  const [status, setStatus] = useState<string>("active");
  const [search, setSearch] = useState("");
  const [assignTaskOpen, setAssignTaskOpen] = useState(false); // T-249c

  const canSeeFinancials = hasRole(user, "manager");

  const { data: projects = [], isLoading } = useQuery({
    queryKey: ["projects_list", status, search],
    queryFn: async () => {
      let q = supabase
        .from("projects")
        .select(`
          project_id, project_code, project_name, status, contract_value,
          scheduled_start_date, scheduled_end_date, risk_rating,
          clients(legal_name, client_code),
          carriers(carrier_code),
          sites(site_code, site_name, suburb),
          pm:employees!projects_project_manager_id_fkey(first_name, last_name)
        `)
        .order("scheduled_start_date", { ascending: false, nullsFirst: false })
        .limit(200);

      if (status === "active") {
        q = q.in("status", ["scheduled", "mobilising", "live", "acceptance_pending"]);
      } else if (status !== "all") {
        // T-162a — STATUS_FILTERS includes "active" and "all" which aren't enum values;
        // when we reach .eq() we know status is a real project_status. Narrow for TS.
        q = q.eq("status", status as Database["public"]["Enums"]["project_status"]);
      }

      if (search.trim()) {
        q = q.or(`project_code.ilike.%${search}%,project_name.ilike.%${search}%`);
      }
      const { data, error } = await q;
      if (error) throw error;
      return data as any[];
    },
  });

  const headers = [
    "Project", "Client", "Site", "Status",
    ...(canSeeFinancials ? ["Value"] : []),
    "Start", "PM", "",
  ];

  return (
    <div className="p-4 md:p-6 max-w-[1400px] mx-auto">
      {/* Back to Project portal */}
      <Link
        to="/project"
        className="inline-flex items-center gap-1.5 text-[12px] text-text-muted hover:text-accent mb-3"
      >
        <ArrowLeft size={14}/> Project portal
      </Link>

      <div className="mb-5 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Projects</h1>
          <div className="text-text-muted text-[13px] mt-1">
            Every job, past and present.
          </div>
        </div>
        {hasRole(user, "manager") && (
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="secondary" icon={Send} onClick={() => setAssignTaskOpen(true)}>
              Assign task
            </Button>
            <Link to="/projects/new"><Button icon={Plus}>New project</Button></Link>
          </div>
        )}
      </div>

      {/* Filter bar */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2 px-3 py-1.5 bg-ink-raised border border-line rounded-md w-full sm:w-80">
          <Search size={14} className="text-text-muted" />
          <input
            placeholder="Search code or name..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="bg-transparent border-none outline-none text-text text-[13px] flex-1"
          />
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {STATUS_FILTERS.map(({ v, label }) => (
            <button
              key={v}
              onClick={() => setStatus(v)}
              className={`px-3 py-1.5 rounded text-[11px] font-mono uppercase tracking-wider2 font-semibold border transition-colors ${
                status === v
                  ? "bg-accent/12 text-accent border-accent/30"
                  : "bg-ink-raised text-text-muted border-line hover:text-text"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Results */}
      {isLoading ? (
        <div className="py-10 flex justify-center"><Spinner /></div>
      ) : projects.length === 0 ? (
        <Card>
          <EmptyState
            title="No projects match"
            description="Try broadening the filter or clearing the search."
            icon={FolderKanban}
          />
        </Card>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b border-line">
                  {headers.map(h => (
                    <th key={h} className="text-left px-4 py-3 text-[10px] font-medium tracking-wider2 uppercase text-text-muted">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {projects.map((p, i) => (
                  <tr
                    key={p.project_id}
                    className={`border-b border-line cursor-pointer hover:bg-ink-hover ${i % 2 ? "bg-ink/40" : ""}`}
                    onClick={() => window.location.href = `/projects/${p.project_id}`}
                  >
                    <td className="px-4 py-3">
                      <div className="font-mono text-[11px] text-accent">{p.project_code}</div>
                      <div className="text-text truncate max-w-[280px]">{p.project_name}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-text">{p.clients?.legal_name ?? "—"}</div>
                      {p.carriers?.carrier_code && (
                        <div className="text-[11px] font-mono text-text-muted">{p.carriers.carrier_code}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {p.sites ? (
                        <>
                          <div className="font-mono text-[11px] text-text-muted">{p.sites.site_code}</div>
                          <div className="text-[11px] text-text-muted truncate max-w-[180px]">{p.sites.suburb}</div>
                        </>
                      ) : <span className="text-text-dim">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <Pill label={p.status.replace(/_/g, " ")} tone={toneFor(p.status)} small />
                    </td>
                    {canSeeFinancials && (
                      <td className="px-4 py-3 font-mono text-text">{fmtMoney(p.contract_value)}</td>
                    )}
                    <td className="px-4 py-3 font-mono text-[12px] text-text-muted">{fmtDate(p.scheduled_start_date)}</td>
                    <td className="px-4 py-3 text-[12px]">
                      {p.pm ? `${p.pm.first_name} ${p.pm.last_name}` : <span className="text-text-dim">—</span>}
                    </td>
                    <td className="px-4 py-3"><ChevronRight size={14} className="text-text-dim" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* T-249c — quick-assign one-off task to an employee */}
      {assignTaskOpen && (
        <AssignPersonalBookingModal
          onClose={() => setAssignTaskOpen(false)}
          onCreated={() => setAssignTaskOpen(false)}
        />
      )}
    </div>
  );
}

function toneFor(status: string): any {
  switch (status) {
    case "scheduled":          return "scheduled";
    case "live":               return "live";
    case "mobilising":         return "mobilising";
    case "acceptance_pending": return "acceptance_pending";
    case "on_hold":            return "on_hold";
    case "closed":             return "closed";
    case "cancelled":          return "cancelled";
    case "won":                return "won";
    case "quoted":             return "quoted";
    default:                   return "default";
  }
}
